import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Menu } from 'primereact/menu';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { 
    markAllNotificationsAsRead, 
    clearAllNotifications, 
    markNotificationAsRead, 
    clearNotification 
} from '../redux/Slices/toastSlice';

function NotificationItem({ notification, onRead, onClear }) {
    const getSeverityIcon = (severity) => {
        switch (severity) {
            case 'success':
                return 'pi-check-circle text-green-500';
            case 'error':
                return 'pi-times-circle text-red-500';
            case 'info':
                return 'pi-info-circle text-blue-500';
            case 'warn':
                return 'pi-exclamation-triangle text-yellow-500';
            default:
                return 'pi-bell text-blue-500';
        }
    };

    const getTimeAgo = (timestamp) => {
        const now = new Date();
        const notificationDate = new Date(timestamp);
        const diffMs = now - notificationDate;
        
        const diffMins = Math.floor(diffMs / (1000 * 60));
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h ago`;
        
        const diffDays = Math.floor(diffHours / 24);
        return `${diffDays}d ago`;
    };

    return (
        <div className={`notification-item p-3 border-bottom-1 border-300 ${!notification.read ? 'bg-primary-50' : ''}`}>
            <div className="flex align-items-start">
                <div className="notification-icon mr-3">
                    <i className={`pi ${getSeverityIcon(notification.severity)} text-xl`}></i>
                </div>
                <div className="notification-content flex-grow-1">
                    <div className="notification-message font-medium mb-1">{notification.summary}</div>
                    <div className="notification-time text-sm text-600">{getTimeAgo(notification.timestamp)}</div>
                </div>
                <div className="notification-actions flex gap-2">
                    {!notification.read && (
                        <Button 
                            icon="pi pi-check" 
                            rounded 
                            text 
                            size="small" 
                            tooltip="Mark as read"
                            onClick={() => onRead(notification.id)}
                        />
                    )}
                    <Button 
                        icon="pi pi-times" 
                        rounded 
                        text 
                        severity="danger" 
                        size="small" 
                        tooltip="Remove"
                        onClick={() => onClear(notification.id)}
                    />
                </div>
            </div>
        </div>
    );
}

export default function Notifications({ notificationRef }) {
    const dispatch = useDispatch();
    const notifications = useSelector((state) => state.toast.notifications);
    const unreadCount = notifications.filter(n => !n.read).length;
    
    const handleMarkAsRead = (id) => {
        dispatch(markNotificationAsRead(id));
    };
    
    const handleClearNotification = (id) => {
        dispatch(clearNotification(id));
    };
    
    const handleMarkAllAsRead = () => {
        dispatch(markAllNotificationsAsRead());
    };
    
    const handleClearAll = () => {
        dispatch(clearAllNotifications());
    };
    
    const notificationItems = notifications.length ? notifications.map(notification => ({
        template: () => (
            <NotificationItem 
                key={notification.id}
                notification={notification}
                onRead={handleMarkAsRead}
                onClear={handleClearNotification}
            />
        )
    })) : [
        {
            template: () => (
                <div className="p-5 text-center">
                    <i className="pi pi-inbox text-4xl text-500 mb-3"></i>
                    <p className="m-0">No new notifications</p>
                </div>
            )
        }
    ];
    
    const notificationHeader = {
        template: () => (
            <div className="flex justify-content-between align-items-center px-3 py-2 border-bottom-1 border-300">
                <div className="flex align-items-center gap-2">
                    <span className="font-medium">Notifications</span>
                    {unreadCount > 0 && <Badge value={unreadCount} severity="danger" />}
                </div>
                <div className="flex gap-2">
                    {notifications.length > 0 && (
                        <>
                            {unreadCount > 0 && (
                                <Button 
                                    label="Mark all read" 
                                    text 
                                    size="small" 
                                    onClick={handleMarkAllAsRead}
                                />
                            )}
                            <Button 
                                label="Clear all" 
                                text 
                                severity="danger" 
                                size="small" 
                                onClick={handleClearAll}
                            />
                        </>
                    )}
                </div>
            </div>
        )
    };
    
    const notificationModel = [notificationHeader, ...notificationItems];
    
    return (
        <Menu 
            model={notificationModel} 
            popup 
            ref={notificationRef} 
            className="notifications-menu" 
            style={{ width: '350px', maxHeight: '500px' }}
        />
    );
}